import { Injectable, inject } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { Observable, Subject, takeUntil } from 'rxjs';
import moment from 'moment';

import { ServiceInvoker } from './service-invoker';
import { SFS_CONFIG } from 'src/config/constants'
import { ContractDetailsService } from './contract-details.service';
import { UtilsService } from './utils.service';
import { logHttpError } from './http-error-logger';

@Injectable({
  providedIn: 'root'
})
export class UploadFileService {
  private readonly dataService = inject(ServiceInvoker);
  private readonly contractDetailsService = inject(ContractDetailsService);
  private readonly utilService = inject(UtilsService);


  private readonly unSubscribeSub: Subject<void> = new Subject();
  // minimal added state for guarding duplicate success & stable naming
  private hasEmittedSuccess = false;
  private batchTimestamp = '';

  pwdProtectedFileCheck(files: any): Observable<any> {
    const uploadFormData = new FormData();
    uploadFormData.append('files', files);
    uploadFormData.append('ROLE', SFS_CONFIG.ROLE);
    uploadFormData.append('SERVICE_ID', SFS_CONFIG.SFS_PASWRD_PROTECTED_PAYLOAD);
    return this.dataService.pwdProtectFile(uploadFormData)
      .pipe(takeUntil(this.unSubscribeSub));
  }

  uploadFiles(allFiles: any, commentData: any, _id?: string): Observable<any> {
    // initialize batch state
    this.hasEmittedSuccess = false;
    this.batchTimestamp = moment(commentData.date).format('MMDDYYYYHHmmss');

    const subject = new Subject();
    const upload = {
      progress: 0,
      status: ''
    };
    const uploadFormData = new FormData();
    const fileMap = this.createFileMap(allFiles, commentData.date);
    const formattedFileNames = Object.keys(fileMap);
    formattedFileNames.forEach( filename =>{
      uploadFormData.append('files', fileMap[filename], filename);
    })

    uploadFormData.append('ROLE', SFS_CONFIG.ROLE);
    uploadFormData.append('submissionType', SFS_CONFIG.SUBMISSION_TYPE);
    uploadFormData.append('postToAWD', 'Y');
    uploadFormData.append('commentsSection', '');
    uploadFormData.append('SERVICE_ID', SFS_CONFIG.SFS_UPLOAD_FILE_PAYLOAD);
    // pass clone to avoid external mutation issues
    this.utilService.setUploadFileNames(formattedFileNames);
    this.dataService.uploadFiles(uploadFormData)
      .pipe(takeUntil(this.unSubscribeSub))
        .subscribe({
          next: (response: any) => {
            if (response['type'] === HttpEventType.UploadProgress) {
              console.log('loaded ', response['loaded'], '   total  -', response['total']);
              const progress = Math.round(100 * response.loaded / response.total);
              console.log(progress);
              upload.progress = progress;
              upload.status = '';
              subject.next(upload);
              return;
            }
            // only treat final HttpResponse as success; guard multiple emissions
            if (response?.type === HttpEventType.Response) {
              const success = response?.body?.responseStatus === 'SUCCESS';
              if (success && !this.hasEmittedSuccess) {
                this.hasEmittedSuccess = true;
                upload.status = 'success';
                upload.progress = 100;
                subject.next(upload);
                subject.complete();
                return;
              }
              if (!success && !this.hasEmittedSuccess) {
                logHttpError('UploadFileService', 'uploadFiles returned non-success response', response, { payload: uploadFormData });
                upload.status = '500';
                subject.next(upload);
                subject.complete();
                return;
              }
            }
          },
          error: (resp: any) => {
            logHttpError('UploadFileService', 'uploadFiles request failed', resp, { payload: uploadFormData });
            upload.status = '500';
            subject.next(upload);
            subject.complete();
          },
        });
    return subject as Observable<any>;
  }

  getFormattedFileName(file: any, date: any): string {
    const contractNumber = this.contractDetailsService.getContractNumber();
    // reuse frozen batch timestamp instead of regenerating per file
    const formattedDate = this.batchTimestamp || moment(date).format('MMDDYYYYHHmmss');
    const extension = file.name?.split('.')?.pop();
    const fileName = file.name?.replace(`.${extension}`, '');
    const strippedFileName = fileName.replace(/[^a-zA-Z0-9]/g, '_');
    const updatedName = `${strippedFileName}-${contractNumber}-${formattedDate}.${extension}`;
    return updatedName;
  }

  /**
  * Creates an object of formatted filenames mapped to files
  * 
  * @param allFiles files for upload
  * @param date date of requirement comment
  */
  createFileMap(allFiles: any, date: string): any {
    let files: any = {}
    allFiles?.forEach((file: File) => {
      const formattedName = this.getFormattedFileName(file, date);
      files[formattedName] = file;
    })
    return files;
  }

}
